import { useRef } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useScroll, useTransform, useInView, animate } from "framer-motion";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Star, Sparkle, LinkedinLogo, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath } from "@/lib/routes";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import { Reveal, Stagger, StaggerItem, LineReveal } from "@/components/motion/Reveal";
import { Marquee } from "@/components/motion/Marquee";
import LeadMagnet from "@/components/LeadMagnet";
import SocialLinks from "@/components/SocialLinks";
import { Testimonials, Reviews } from "@/components/SocialProof";
import { breadcrumbSchema, personSchema } from "@/lib/schema";

const HERO_IMG = null; // Deprecated — hero now uses Magnus's portrait via profile.avatar.

const C = {
  da: {
    eyebrow: "Magnus Løv Schmidt — Odense",
    title: ["SEO specialist der", "flytter omsætning."],
    desc: "Jeg skaber organisk vækst, der kan mærkes på bundlinjen — ikke ligegyldige tal i en rapport. Teknisk SEO, on-page, linkbuilding og strategi, samlet ét sted.",
    cta1: "Lad os snakke", cta2: "Se mine ydelser",
    rated: "Elsket af de fleste, hadet af nogle få",
    stats: [
      { n: 15, s: "+", l: "Brancher arbejdet med" },
      { n: 99, s: "+", l: "Sider analyseret" },
      { n: 1, s: " mio+", l: "Besøgende skabt" },
    ],
    servicesEyebrow: "Ydelser",
    servicesTitle: "SEO der dækker hele rejsen",
    servicesDesc: "Fra det tekniske fundament til strategi og indhold. Vælg den disciplin, der matcher din udfordring.",
    manifestoEyebrow: "Derfor mig",
    manifestoTitle: "Tre principper jeg arbejder efter",
    manifesto: [
      { t: "No-bullshit rådgivning", d: "Jeg giver dig sandheden uden fancy termer, som ingen forstår. SEO ned på et sprog, hvor du er med i alt, hvad jeg laver." },
      { t: "Holistisk tilgang", d: "SEO er kun én del af marketingspillet. Jeg har fokus på, at hele din forretning spiller — ikke bare placeringerne." },
      { t: "Forrest inden for viden", d: "Løbende overvågning af nye tendenser og strategier. Altid testet på egne projekter først, før det rammer dig." },
    ],
    blogEyebrow: "Viden",
    blogTitle: "Seneste artikler",
    readMore: "Læs alle artikler",
    projEyebrow: "Projekter",
    projTitle: "Ting jeg bygger ved siden af",
    projDesc: "Jeg er involveret i flere projekter — fra linkbuilding og ecommerce til det kreative. Udforsk dem alle.",
    projAll: "Alle projekter",
    stackEyebrow: "Tech Stacks",
    stackTitle: "Værktøjerne bag arbejdet",
    stackDesc: "De værktøjer jeg faktisk bruger inden for SEO, marketing, sociale medier, content og produktivitet.",
    stackAll: "Alle tech stacks",
    someEyebrow: "Følg med",
    someTitle: "Vi ses på de sociale medier",
    someDesc: "Jeg deler viden, projekter og tanker om SEO på tværs af platforme. Mit LinkedIn-nyhedsbrev udkommer løbende — connect og følg med.",
    marqueeWords: ["SEO SPECIALIST", "TEKNISK SEO", "LINKBUILDING", "ECOMMERCE SEO", "SEO STRATEGI", "LOKAL SEO"],
    clientEyebrow: "Kunder & samarbejder",
    clientTitle: "Brands jeg har hjulpet med SEO",
    clientDesc: "Fra ecommerce og SaaS til foreninger og etablerede brands — organisk vækst leveret hos:",
    clients: ["Superb", "Ældresagen", "Norlys", "Vela", "BLITE", "Onyx Cookware"],
    linkedinStat: "8.000+ følger med på LinkedIn",
    newsletterStat: "500+ læser mit nyhedsbrev",
    linkedinCta: "Connect og følg med",
    portraitAlt: "Magnus Løv Schmidt — SEO Specialist i Odense",
    seoEyebrow: "SEO specialist",
    seoTitle: "Freelance SEO specialist med fokus på reel omsætning",
    seoParas: [
      "Som freelance SEO specialist i Odense hjælper jeg både danske og internationale virksomheder med at vokse organisk. Fra små webshops og SaaS-startups til etablerede brands som Superb, Ældresagen og Norlys — arbejdet er altid det samme: at gøre din SEO til en indtægtskanal, ikke en trafik-KPI uden bundlinje.",
      "Til forskel fra et bureau er du ikke én kunde blandt hundrede — du får en dedikeret SEO specialist, der selv sidder med hænderne i teknik, indhold og strategi. Ingen mellemled, ingen skiftende juniorer, ingen skjulte lag. Bare direkte adgang til dén person, der laver arbejdet, og som står inde for det.",
      "Min tilgang bygger på tre ting: en klar prioritering af det, der reelt flytter forretningen, en gennemtænkt fordeling mellem teknisk fundament, on-page og autoritet, og transparent løbende rapportering, hvor du altid ved, hvor pengene går hen. Det er derfor mine kunder bliver.",
      "En freelance SEO specialist giver dig fleksibiliteten fra et bureau, kombineret med dybden fra en intern specialist — uden ansættelsesomkostningerne og uden fast binding. Om du har brug for et intensivt teknisk løft, en langsigtet SEO-partner eller en sparringspartner på strategien: jeg tilpasser samarbejdet til dét, din forretning kræver lige nu.",
    ],
    seoListTitle: "Hvad du får som SEO specialist-kunde",
    seoList: [
      "Direkte adgang — én person, ét telefonnummer, hurtige svar",
      "Skræddersyet plan der prioriterer det, der flytter mest",
      "Transparente rapporter uden bureaupolyfyld eller vanity-tal",
      "Månedlig sparring og løbende justeringer, når data ændrer sig",
      "Dyb ekspertise inden for teknisk SEO, on-page og linkbuilding",
      "Ingen fast binding — værdi hvert kvartal, eller vi stopper",
    ],
  },
  en: {
    eyebrow: "Magnus Løv Schmidt — Odense",
    title: ["Danish SEO specialist", "who moves revenue."],
    desc: "I'm a Danish SEO specialist creating organic growth that shows on the bottom line — not vanity numbers in a report. Technical SEO, on-page, link building and strategy, all in one place.",
    cta1: "Let's talk", cta2: "See my services",
    rated: "Loved by most, hated by a few",
    stats: [
      { n: 15, s: "+", l: "Industries worked with" },
      { n: 99, s: "+", l: "Sites analysed" },
      { n: 1, s: "M+", l: "Visitors created" },
    ],
    servicesEyebrow: "Services",
    servicesTitle: "SEO that covers the whole journey",
    servicesDesc: "From the technical foundation to strategy and content. Pick the discipline that matches your challenge.",
    manifestoEyebrow: "Why me",
    manifestoTitle: "Three principles I work by",
    manifesto: [
      { t: "No-bullshit advice", d: "I give you the truth without fancy terms nobody understands. SEO in a language where you follow everything I do." },
      { t: "Holistic approach", d: "SEO is only one part of the marketing game. I focus on your whole business performing — not just rankings." },
      { t: "Ahead on knowledge", d: "Constant monitoring of new trends and strategies. Always tested on my own projects first, before it reaches you." },
    ],
    blogEyebrow: "Blog",
    blogTitle: "Latest articles",
    readMore: "View all articles",
    projEyebrow: "Projects",
    projTitle: "Things I build on the side",
    projDesc: "I'm involved in several projects — from link building and ecommerce to the creative. Explore them all.",
    projAll: "All projects",
    stackEyebrow: "Tech Stacks",
    stackTitle: "The tools behind the work",
    stackDesc: "The tools I actually use across SEO, marketing, social media, content and productivity.",
    stackAll: "All tech stacks",
    someEyebrow: "Follow along",
    someTitle: "Let's connect on social",
    someDesc: "I share knowledge, projects and thoughts on SEO across platforms. My LinkedIn newsletter goes out regularly — connect and follow along.",
    marqueeWords: ["SEO SPECIALIST", "TECHNICAL SEO", "LINK BUILDING", "ECOMMERCE SEO", "SEO STRATEGY", "LOCAL SEO"],
    clientEyebrow: "Clients & collaborations",
    clientTitle: "Brands I've helped with SEO",
    clientDesc: "From ecommerce and SaaS to nonprofits and established brands — organic growth delivered at:",
    clients: ["Superb", "Ældresagen", "Norlys", "Vela", "BLITE", "Onyx Cookware"],
    linkedinStat: "8,000+ following on LinkedIn",
    newsletterStat: "500+ read my newsletter",
    linkedinCta: "Connect and follow along",
    portraitAlt: "Magnus Løv Schmidt — SEO Specialist based in Odense",
    seoEyebrow: "SEO specialist",
    seoTitle: "Freelance SEO specialist focused on real revenue",
    seoParas: [
      "As a freelance SEO specialist based in Odense, I help both Danish and international businesses grow organically. From small stores and SaaS startups to established brands like Superb, Ældresagen and Norlys — the work is always the same: turn SEO into a revenue channel, not a traffic KPI without a bottom line.",
      "Unlike an agency, you're not one client among a hundred — you get a dedicated SEO specialist with their hands directly in tech, content and strategy. No middlemen, no rotating juniors, no hidden layers. Just direct access to the person doing the work, who stands behind it.",
      "My approach rests on three things: a clear prioritisation of what actually moves the business, a considered balance between technical foundation, on-page and authority, and transparent ongoing reporting where you always know where the money goes. It's why my clients stay.",
      "A freelance SEO specialist gives you the flexibility of an agency combined with the depth of an in-house specialist — without the hiring costs and without long-term lock-in. Whether you need an intense technical lift, a long-term SEO partner or a sparring partner on strategy: I adapt to what your business actually needs right now.",
    ],
    seoListTitle: "What you get as an SEO specialist client",
    seoList: [
      "Direct access — one person, one phone number, fast replies",
      "Tailored plan prioritising what moves the needle most",
      "Transparent reporting without agency filler or vanity numbers",
      "Monthly sparring and ongoing adjustments as data changes",
      "Deep expertise across technical SEO, on-page and link building",
      "No long-term lock-in — value every quarter, or we stop",
    ],
  },
};

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (inView) {
      const controls = animate(0, to, { duration: 1.4, ease: "easeOut", onUpdate: (v) => setVal(Math.round(v)) });
      return () => controls.stop();
    }
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

export default function Home() {
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const c = C[lang];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: api.getServices });
  const { data: posts = [] } = useQuery({ queryKey: ["blog"], queryFn: api.getBlog });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: api.getProjects });
  const { data: stacks = [] } = useQuery({ queryKey: ["stacks"], queryFn: api.getTechStacks });

  const jsonLd = [
    personSchema(profile || {}),
    breadcrumbSchema([{ name: "Home", path: buildPath("home", lang) }]),
  ];

  return (
    <>
      <Seo
        routeKey="home"
        lang={lang}
        title={lang === "da"
          ? "SEO Specialist i Odense | Magnus Løv Schmidt"
          : "SEO Specialist in Odense | Magnus Løv Schmidt"}
        description={c.desc}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden border-b border-border px-6 md:px-12 lg:px-20 pt-16 pb-20 md:pt-24 md:pb-28">
        <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-primary/10 blur-[120px]" />

        <div className="relative grid gap-12 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] lg:items-start">
          {/* LEFT — copy */}
          <div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="label-mono mb-6 text-primary">
              {c.eyebrow}
            </motion.div>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-[5.5rem] font-black tracking-tighter leading-[0.92]">
              <LineReveal lines={c.title} accentIndex={1} />
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-8 max-w-xl text-lg md:text-xl text-muted-foreground font-light leading-relaxed">
              {c.desc}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
              className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to={buildPath("contact", lang)} data-testid="hero-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-md bg-primary px-7 py-4 font-medium text-primary-foreground transition-transform hover:scale-[1.04]">
                {c.cta1}
                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to={buildPath("services", lang)} data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-border px-7 py-4 font-medium transition-colors hover:border-primary hover:text-primary">
                {c.cta2}
              </Link>
            </motion.div>

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-3">
                <div className="flex text-primary">
                  {[...Array(5)].map((_, i) => <Star key={i} size={16} weight="fill" />)}
                </div>
                <span className="text-sm text-muted-foreground">{c.rated}</span>
              </div>
              <a href={profile?.linkedin} target="_blank" rel="noopener noreferrer" data-testid="hero-linkedin-stat"
                className="group inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                <LinkedinLogo size={14} weight="fill" />
                <span className="font-mono">{c.linkedinStat}</span>
                <ArrowUpRight size={11} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a href={profile?.linkedin_newsletter || profile?.linkedin} target="_blank" rel="noopener noreferrer" data-testid="hero-newsletter-stat"
                className="group inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                <Sparkle size={14} weight="fill" />
                <span className="font-mono">{c.newsletterStat}</span>
                <ArrowUpRight size={11} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </motion.div>
          </div>

          {/* RIGHT — portrait (desktop only; hidden on mobile/tablet so heading owns the fold) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8, ease: [0.19, 1, 0.22, 1] }}
            style={{ y: imgY, scale: imgScale }}
            className="relative mx-auto hidden w-full max-w-sm lg:block lg:max-w-none"
            data-testid="hero-portrait"
          >
            <div className="pointer-events-none absolute -inset-4 rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-transparent blur-2xl" aria-hidden="true" />
            <div className="relative overflow-hidden rounded-2xl border border-border bg-secondary">
              <img
                src={profile?.avatar}
                alt={c.portraitAlt}
                className="aspect-[4/5] h-full w-full object-cover"
                loading="eager"
                fetchPriority="high"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 to-transparent" aria-hidden="true" />
              <figcaption className="absolute inset-x-0 bottom-0 flex flex-col gap-1 p-5 text-white">
                <span className="label-mono text-[10px] text-white/70">Magnus Løv Schmidt</span>
                <span className="font-heading text-lg font-bold leading-tight">{lang === "da" ? "SEO Specialist · Odense" : "SEO Specialist · Odense"}</span>
              </figcaption>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-b border-border py-8">
        <Marquee>
          {c.marqueeWords.map((w, i) => (
            <span key={i} className="mx-8 font-heading text-6xl md:text-8xl font-black text-stroke">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* CLIENT LOGOS / SOCIAL PROOF */}
      <section data-testid="client-proof" className="border-b border-border px-6 md:px-12 lg:px-20 py-14">
        <Reveal>
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:items-end">
            <div>
              <div className="label-mono mb-3 text-muted-foreground/60">{c.clientEyebrow}</div>
              <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight leading-tight">{c.clientTitle}</h2>
              <p className="mt-3 max-w-md text-sm text-muted-foreground font-light leading-relaxed">{c.clientDesc}</p>
            </div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-6">
              {c.clients.map((name) => (
                <li key={name} data-testid={`client-logo-${name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex h-12 items-center justify-center rounded-md border border-border bg-secondary/40 px-3 text-center font-heading text-sm font-bold tracking-tight text-muted-foreground transition-colors hover:border-primary hover:text-foreground">
                  {name}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* SEO SPECIALIST — keyword-focused positioning */}
      <section data-testid="seo-specialist" className="border-b border-border px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="grid gap-12 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
            <div>
              <div className="label-mono mb-4 text-primary">{c.seoEyebrow}</div>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-tight max-w-2xl">
                {c.seoTitle}
              </h2>
              <div className="mt-8 space-y-5 max-w-2xl text-muted-foreground font-light leading-relaxed">
                {c.seoParas.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
            <aside className="lg:pt-16">
              <div className="rounded-2xl border border-border bg-secondary/40 p-7">
                <div className="label-mono mb-5 text-muted-foreground/70">{c.seoListTitle}</div>
                <ul className="space-y-3">
                  {c.seoList.map((item, i) => (
                    <li key={i} data-testid={`seo-benefit-${i}`} className="flex gap-3 text-sm">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>
        </Reveal>
      </section>

      {/* STATS */}
      <section className="border-b border-border px-6 md:px-12 lg:px-20 py-16">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {c.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="border-l-2 border-primary pl-6">
                <div className="font-heading text-5xl md:text-6xl font-black tracking-tighter">
                  <Counter to={s.n} suffix={s.s} />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="label-mono mb-4 text-primary">{c.servicesEyebrow}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl">{c.servicesTitle}</h2>
          <p className="mt-4 max-w-xl text-muted-foreground font-light">{c.servicesDesc}</p>
        </Reveal>
        <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.slice(0, 9).map((s, i) => {
            const cc = s[lang] || s.da;
            return (
              <StaggerItem key={s.slug}>
                <Link to={buildPath("service", lang, { slug: s.slug })} data-testid={`home-service-${s.slug}`}
                  className="card-hover group flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6">
                  <div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-muted-foreground">0{i + 1}</span>
                      <ArrowUpRight size={20} className="text-muted-foreground transition-all group-hover:text-primary group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                    <h3 className="mt-6 font-heading text-2xl font-bold tracking-tight">{cc.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">{cc.tagline}</p>
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* MANIFESTO */}
      <section className="border-y border-border px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="label-mono mb-4 text-primary">{c.manifestoEyebrow}</div>
          <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl">{c.manifestoTitle}</h2>
        </Reveal>
        <div className="mt-12 grid gap-px overflow-hidden rounded-xl border border-border bg-border md:grid-cols-3">
          {c.manifesto.map((m, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="h-full bg-card p-8">
                <div className="font-heading text-6xl font-black text-primary/20">0{i + 1}</div>
                <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight">{m.t}</h3>
                <p className="mt-3 text-sm text-muted-foreground font-light leading-relaxed">{m.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <Testimonials profile={profile} />
      <Reviews profile={profile} />

      {/* PROJECTS teaser */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <div className="label-mono mb-4 text-muted-foreground/60">{c.projEyebrow}</div>
              <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl">{c.projTitle}</h2>
              <p className="mt-4 max-w-xl text-muted-foreground font-light">{c.projDesc}</p>
            </div>
            <Link to={buildPath("projects", lang)} className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
              {c.projAll} <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </Reveal>
        <Stagger className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {projects.map((p) => {
            const cc = p[lang] || p.da;
            const to = p.external_only ? null : buildPath("project", lang, { slug: p.slug });
            const inner = (
              <>
                <span className="label-mono text-muted-foreground/70">{cc.title}</span>
                <p className="mt-4 font-heading text-lg font-bold leading-snug">{cc.tagline}</p>
                <ArrowUpRight size={18} className="mt-4 text-muted-foreground transition-all group-hover:text-foreground group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            );
            return (
              <StaggerItem key={p.slug}>
                {to ? (
                  <Link to={to} data-testid={`home-project-${p.slug}`} className="card-hover group flex h-full flex-col rounded-xl border border-border bg-card p-6">{inner}</Link>
                ) : (
                  <a href={p.external_url} target="_blank" rel="noopener noreferrer" data-testid={`home-project-${p.slug}`} className="card-hover group flex h-full flex-col rounded-xl border border-border bg-card p-6">{inner}</a>
                )}
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* TECH STACKS teaser */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <div className="label-mono mb-4 text-muted-foreground/60">{c.stackEyebrow}</div>
              <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight max-w-2xl">{c.stackTitle}</h2>
              <p className="mt-4 max-w-xl text-muted-foreground font-light">{c.stackDesc}</p>
            </div>
            <Link to={buildPath("techstacks", lang)} className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground">
              {c.stackAll} <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </Reveal>
        <Stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {stacks.map((s) => {
            const cc = s[lang] || s.da;
            return (
              <StaggerItem key={s.slug}>
                <Link to={buildPath("techstack", lang, { slug: s.slug })} data-testid={`home-stack-${s.slug}`}
                  className="card-hover group flex h-full flex-col justify-between rounded-xl border border-border bg-card p-6">
                  <span className="font-heading text-lg font-bold tracking-tight leading-snug">{cc.title}</span>
                  <span className="mt-6 font-mono text-xs text-muted-foreground">{s.tools?.length} {t("tools_count")}</span>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* BLOG */}
      <section className="px-6 md:px-12 lg:px-20 py-20">
        <Reveal>
          <div className="flex items-end justify-between">
            <div>
              <div className="label-mono mb-4 text-primary">{c.blogEyebrow}</div>
              <h2 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight">{c.blogTitle}</h2>
            </div>
            <Link to={buildPath("blog", lang)} className="hidden md:inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary">
              {c.readMore} <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </Reveal>
        <Stagger className="mt-10 grid gap-6 md:grid-cols-3">
          {posts.slice(0, 3).map((p) => {
            const cc = p[lang] || p.da;
            return (
              <StaggerItem key={p.slug}>
                <Link to={buildPath("post", lang, { slug: p.slug })} data-testid={`home-post-${p.slug}`}
                  className="group block overflow-hidden rounded-xl border border-border bg-card card-hover">
                  <div className="grayscale-hover aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={cc.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="rounded-full bg-secondary px-2.5 py-1 text-primary">{p.category}</span>
                      <span className="font-mono">{p.read_time} {t("min_read")}</span>
                    </div>
                    <h3 className="mt-4 font-heading text-xl font-bold tracking-tight leading-snug">{cc.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground font-light line-clamp-2">{cc.excerpt}</p>
                  </div>
                </Link>
              </StaggerItem>
            );
          })}
        </Stagger>
      </section>

      {/* NEWSLETTER FEATURE — dedicated, high-conversion block for the
          LinkedIn "If I was X" newsletter. Text-only so nothing needs to be
          kept in sync with the actual weekly issues. */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-24">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="label-mono mb-4 text-primary" data-testid="newsletter-eyebrow">
              {lang === "da" ? "Nyhedsbrev · If I was X" : "Newsletter · If I was X"}
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.95]">
              {lang === "da"
                ? "Mini SEO-audits på rigtige brands. Hver uge. Gratis."
                : "Mini SEO audits on real brands. Every week. Free."}
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground font-light leading-relaxed">
              {lang === "da"
                ? "Hver uge tager jeg en rigtig virksomhed — ofte en du kender — og laver et 5-minutters SEO-audit. Tre observationer, ét konkret take-away du kan bruge samme dag. Kort, direkte, uden fluff."
                : "Every week I take a real company — often one you know — and run a 5-minute SEO audit. Three observations, one concrete takeaway you can use the same day. Short, direct, no fluff."}
            </p>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                lang === "da"
                  ? "Én rigtig virksomhed pr. udgave — dansk eller international"
                  : "One real company per issue — Danish or international",
                lang === "da"
                  ? "Tre konkrete observationer, jeg selv ville fixe først"
                  : "Three concrete observations I'd fix first",
                lang === "da"
                  ? "Ét praktisk take-away til dit eget site samme dag"
                  : "One practical takeaway for your own site the same day",
                lang === "da"
                  ? "5 minutters læsning — respekt for din indbakke"
                  : "5 minutes of reading — respect for your inbox",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm md:text-base text-foreground/90 font-light">
                  <Check size={18} weight="bold" className="mt-0.5 shrink-0 text-primary" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <a
                href={profile?.linkedin_newsletter}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="home-newsletter-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-medium text-primary-foreground transition-transform hover:scale-[1.04]"
              >
                {lang === "da" ? "Abonnér gratis på LinkedIn" : "Subscribe free on LinkedIn"}
                <ArrowUpRight size={16} weight="bold" />
              </a>
              <span className="label-mono text-muted-foreground/70">
                {lang === "da" ? "500+ læsere · udkommer ugentligt" : "500+ readers · weekly"}
              </span>
            </div>
            <div className="mt-8">
              <SocialLinks columns="grid-cols-3 sm:grid-cols-6" />
            </div>
          </Reveal>
        </div>
      </section>

      <LeadMagnet profile={profile} />
    </>
  );
}
